import { Fragment } from "react";
import { Link } from "@inertiajs/react";
import { SearchIcon, LoaderPinwheelIcon } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import usePage from "@/hooks/usePage";
import { usePublicationSubdomain } from "@/hooks/usePublicationSubdomain";
import useContentEntityStore from "@/hooks/useContentEntityStore";

export function TopBar() {
  const { processing } = useContentEntityStore();
  const { props } = usePage<{ user: any } & AdminUsePageProps>();
  const subdomain = usePublicationSubdomain();

  return (
    <header className="relative flex items-center py-3 px-6 overflow-hidden col-span-2">
      <h1>
        <Link
          href={subdomain}
          className="relative flex items-center gap-0.5 text-2xl font-semibold text-primary tracking-tight group"
        >
          b
          <span className="inline-block duration-700 transition-all overflow-hidden max-w-0 group-hover:max-w-16">
            a
          </span>
          <span className="inline-block duration-700 transition-all overflow-hidden max-w-0 group-hover:max-w-16">
            l
          </span>
          <span className="-ml-1.5 transition-all duration-700 group-hover:-ml-0.5">
            lp
          </span>
          <span className="inline-block duration-700 transition-all overflow-hidden max-w-0 group-hover:max-w-16">
            o
          </span>
          <span className="inline-block duration-700 transition-all overflow-hidden max-w-0 group-hover:max-w-16">
            i
          </span>
          <span className="-ml-1.5 transition-all duration-700 group-hover:-ml-0">
            nt
          </span>
        </Link>
      </h1>
      {processing && (
        <div className="absolute top-1/2 left-[240px] -translate-y-1/2 text-gray-400 flex items-center gap-1 text-sm">
          <LoaderPinwheelIcon className="size-4 animate-spin" />
          Saving...
        </div>
      )}
      <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-sm text-gray-500 flex items-center gap-1">
        <Breadcrumb>
          <BreadcrumbList>
            {props.breadcrumbs.map((breadcrumb, idx) => (
              <Fragment key={`${breadcrumb.path}-${idx}`}>
                <BreadcrumbItem>
                  {breadcrumb.current ? (
                    <BreadcrumbPage className="cursor-default">
                      {breadcrumb.name}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink href={breadcrumb.path}>
                      {breadcrumb.name}
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {idx < props.breadcrumbs.length - 1 && <BreadcrumbSeparator />}
              </Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </span>
      <div className="ml-auto flex items-center gap-4">
        <button
          type="button"
          className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200"
        >
          <SearchIcon size={16} />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="cursor-pointer rounded bg-primary data-[state=open]:contrast-80 overflow-hidden data-[state=open]:ring-[3px] data-[state=open]:ring-ring/50"
            >
              <div className="w-7 h-7" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <form action="/users/sign_out" method="POST">
                <input
                  type="hidden"
                  name="authenticity_token"
                  value={props.csrf_token}
                />
                <input type="hidden" name="_method" value="delete" />
                <button type="submit">Logout</button>
              </form>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
